if (!customElements.get('gift-bundle-builder')) {
  class GiftBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      this.drops = Array.from(this.querySelectorAll('[data-drop]'));
      this.slots = this.querySelector('[data-slots]');
      this.statusEl = this.querySelector('[data-status]');
      this.compareEl = this.querySelector('[data-compare]');
      this.totalEl = this.querySelector('[data-total]');
      this.addButton = this.querySelector('[data-add]');
      this.messageInput = this.querySelector('[data-gift-message]');
      this.errorEl = this.querySelector('[data-error]');
      this.selected = [];
      this.notice = '';
      this.loading = false;

      this.tiers.forEach((tier) => tier.addEventListener('click', () => this.setTier(tier)));
      this.drops.forEach((drop) => drop.addEventListener('click', () => this.toggleDrop(drop)));
      this.addButton.addEventListener('click', () => this.addToCart());

      const activeTier = this.tiers.find((tier) => tier.getAttribute('aria-pressed') === 'true') || this.tiers[0];
      this.setTier(activeTier);
    }

    setTier(tier) {
      this.size = Number(tier.dataset.size);
      this.discount = Number(tier.dataset.discount);
      this.discountCode = tier.dataset.code || '';
      this.tiers.forEach((t) => t.setAttribute('aria-pressed', String(t === tier)));
      // Switching from 6 down to 3 keeps the first picks
      this.selected = this.selected.slice(0, this.size);
      this.notice = '';
      this.render();
    }

    toggleDrop(drop) {
      const index = this.selected.indexOf(drop);
      if (index > -1) {
        this.selected.splice(index, 1);
        this.notice = '';
      } else if (this.selected.length < this.size) {
        this.selected.push(drop);
        this.notice = '';
      } else {
        this.notice = 'Your bundle is full. Tap a selected drop to swap it out.';
      }
      this.render();
    }

    render() {
      const count = this.selected.length;
      const remaining = this.size - count;

      this.drops.forEach((drop) => {
        const isSelected = this.selected.includes(drop);
        drop.setAttribute('aria-pressed', String(isSelected));
        drop.classList.toggle('is-dimmed', !isSelected && remaining === 0);
      });

      this.slots.replaceChildren(
        ...Array.from({ length: this.size }, (_, i) => {
          const slot = document.createElement('span');
          slot.className = 'gift-bundle__slot';
          const drop = this.selected[i];
          if (drop) {
            slot.classList.add('is-filled');
            if (drop.dataset.thumb) {
              const img = document.createElement('img');
              img.src = drop.dataset.thumb;
              img.alt = '';
              slot.append(img);
            }
          }
          return slot;
        })
      );

      const total = this.selected.reduce((sum, drop) => sum + Number(drop.dataset.price), 0);
      const discounted = Math.round((total * (100 - this.discount)) / 100);
      this.compareEl.textContent = count && this.discount ? this.formatMoney(total) : '';
      this.totalEl.textContent = count ? this.formatMoney(discounted) : '';

      if (this.notice) {
        this.statusEl.textContent = this.notice;
      } else if (remaining > 0) {
        this.statusEl.textContent = `${count} of ${this.size} selected · pick ${remaining} more to save ${this.discount}%`;
      } else {
        this.statusEl.textContent = `Bundle complete · ${this.discount}% off applied in your cart`;
      }

      if (!this.loading) {
        this.addButton.disabled = remaining !== 0;
        this.addButton.textContent =
          remaining > 0 ? `Pick ${remaining} more drop${remaining === 1 ? '' : 's'}` : `Add ${this.size} drops to cart`;
      }
    }

    setLoading(loading) {
      this.loading = loading;
      this.addButton.setAttribute('aria-busy', String(loading));
      if (loading) {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Adding…';
      } else {
        this.render();
      }
    }

    async addToCart() {
      if (this.loading || this.selected.length !== this.size) return;
      this.setLoading(true);
      this.errorEl.textContent = '';

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartDrawer = document.querySelector('cart-drawer');
      const canUseDrawer =
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        typeof cartDrawer.getSectionsToRender === 'function';

      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      const addBody = {
        items: this.selected.map((drop) => ({
          id: Number(drop.dataset.variantId),
          quantity: 1,
          properties: { 'Gift bundle': `${this.size}-drop bundle` },
        })),
      };

      // Applying the code replaces any code already on the cart, which is what we want here
      const updateBody = {};
      if (this.discountCode) updateBody.discount = this.discountCode;
      const message = this.messageInput ? this.messageInput.value.trim() : '';
      if (message) updateBody.attributes = { 'Gift message': message };
      if (canUseDrawer) {
        updateBody.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        updateBody.sections_url = window.location.pathname;
      }

      try {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify(addBody),
        });
        const addData = await addResponse.json();
        if (!addResponse.ok) {
          throw new Error(addData.description || addData.message || 'Something went wrong. Please try again.');
        }

        // Re-render the drawer from this response so it shows the discount
        const updateResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify(updateBody),
        });
        const updateData = updateResponse.ok ? await updateResponse.json() : null;

        if (canUseDrawer && updateData && updateData.sections) {
          try {
            cartDrawer.renderContents(updateData);
            this.setLoading(false);
            return;
          } catch (error) {
            // Drawer didn't accept the response, fall through to the cart page
          }
        }

        // The /discount/ link applies the code as a backup if the cart update didn't
        window.location.href = this.discountCode
          ? `${root}discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(`${root}cart`)}`
          : `${root}cart`;
      } catch (error) {
        this.errorEl.textContent = error.message;
        this.setLoading(false);
      }
    }

    formatMoney(cents) {
      const value = cents / 100;
      const format = (decimals, thousands = ',', decimal = '.') => {
        const [whole, fraction] = value.toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      };
      const formats = {
        amount: () => format(2),
        amount_no_decimals: () => format(0),
        amount_with_comma_separator: () => format(2, '.', ','),
        amount_no_decimals_with_comma_separator: () => format(0, '.', ','),
        amount_with_apostrophe_separator: () => format(2, "'", '.'),
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
    }
  }

  customElements.define('gift-bundle-builder', GiftBundleBuilder);
}
